import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Play, CheckCircle, AlertCircle, BookOpen, Plus, Trash2, Send, Bell, ShieldCheck, XCircle, Settings, Power, RefreshCw, Image as ImageIcon, Loader2, X, Pencil, Archive, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import SocialArticleCreator from './SocialArticleCreator';
import ArticleEditor from './ArticleEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogTab = () => {
  const [blogArticles, setBlogArticles] = useState([]);
  const [blogMsg, setBlogMsg] = useState('');
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [testEmailResult, setTestEmailResult] = useState(null);
  const [blogSchedulerStatus, setBlogSchedulerStatus] = useState(null);
  const [triggeringBlog, setTriggeringBlog] = useState(false);
  const [notifStats, setNotifStats] = useState(null);
  const [notifLogs, setNotifLogs] = useState([]);
  const [emailDiagnostics, setEmailDiagnostics] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [digestStatus, setDigestStatus] = useState(null);
  const [triggeringDigest, setTriggeringDigest] = useState(false);
  const [fbMonitoring, setFbMonitoring] = useState(false);
  const [fbMonitorStatus, setFbMonitorStatus] = useState(null);
  const [fbConfigSaving, setFbConfigSaving] = useState(false);
  // Image editing state
  const [editingImageArticle, setEditingImageArticle] = useState(null);
  const [editImageOptions, setEditImageOptions] = useState([]);
  const [editImageLoading, setEditImageLoading] = useState(false);
  const [editImageSelected, setEditImageSelected] = useState(null);
  const [editImageProgress, setEditImageProgress] = useState(0);
  // Article editor state
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleFilter, setArticleFilter] = useState('all');

  const fetchBlogArticles = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/blog/admin/articles`);
      setBlogArticles(res.data.articles);
    } catch (err) { console.error(err); }
  }, []);

  const fetchBlogSchedulerStatus = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/blog/admin/scheduler-status`);
      setBlogSchedulerStatus(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchNotificationData = useCallback(async () => {
    try {
      const [statsRes, logsRes, digestRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/blog/admin/notification-stats`),
        axios.get(`${BACKEND_URL}/api/blog/admin/notification-logs?limit=20`),
        axios.get(`${BACKEND_URL}/api/blog/admin/digest-status`),
      ]);
      setNotifStats(statsRes.data);
      setNotifLogs(logsRes.data.logs || []);
      setDigestStatus(digestRes.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchEmailDiagnostics = async () => {
    setDiagLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/blog/admin/email-diagnostics`);
      setEmailDiagnostics(res.data);
    } catch (err) { console.error(err); }
    finally { setDiagLoading(false); }
  };

  const fetchFbMonitorStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/blog/admin/fb-monitor-status`);
      setFbMonitorStatus(res.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    fetchBlogArticles();
    fetchBlogSchedulerStatus();
    fetchNotificationData();
    fetchFbMonitorStatus();
  }, [fetchBlogArticles, fetchNotificationData, fetchFbMonitorStatus]);

  const handleTriggerBlogGenerate = async () => {
    setTriggeringBlog(true);
    setBlogMsg('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/blog/admin/trigger-generate`);
      setBlogMsg(`Artikel "${res.data.article_title}" berhasil di-generate dan dipublish!`);
      fetchBlogArticles();
      fetchBlogSchedulerStatus();
    } catch (err) {
      setBlogMsg('Gagal: ' + (err.response?.data?.detail || err.message));
    } finally { setTriggeringBlog(false); }
  };

  const handlePublishArticle = async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/blog/admin/articles/${id}`, { status: 'published' });
      setBlogMsg('Artikel berhasil di-publish!');
      fetchBlogArticles();
    } catch (err) { setBlogMsg('Gagal publish: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleArchiveArticle = async (id) => {
    if (!window.confirm('Arsipkan artikel ini? Artikel tidak akan tampil di website.')) return;
    try {
      await axios.put(`${BACKEND_URL}/api/blog/admin/articles/${id}`, { status: 'archived' });
      setBlogMsg('Artikel diarsipkan.');
      fetchBlogArticles();
    } catch (err) { setBlogMsg('Gagal arsipkan: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleUnpublishArticle = async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/blog/admin/articles/${id}`, { status: 'draft' });
      setBlogMsg('Artikel dikembalikan ke draft.');
      fetchBlogArticles();
    } catch (err) { console.error(err); }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Hapus artikel ini?')) return;
    try { await axios.delete(`${BACKEND_URL}/api/blog/admin/articles/${id}`); fetchBlogArticles(); } catch (err) { console.error(err); }
  };

  const handleEditImage = async (article) => {
    setEditingImageArticle(article);
    setEditImageOptions([]);
    setEditImageSelected(null);
    setEditImageLoading(true);
    setEditImageProgress(0);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/blog/admin/generate-article-images`, {
        article_id: article.id, title: article.title, excerpt: article.excerpt || '', tags: article.tags || [],
      });
      const jobId = res.data.job_id;
      // Poll for images
      for (let i = 0; i < 90; i++) {
        setEditImageProgress(Math.min(95, Math.round((i / 60) * 100)));
        await new Promise(r => setTimeout(r, 2000));
        try {
          const poll = await axios.get(`${BACKEND_URL}/api/blog/admin/image-job/${jobId}`);
          if (poll.data.status === 'images_ready') {
            setEditImageOptions(poll.data.image_urls.map((url, idx) => ({
              id: poll.data.image_ids[idx], url: url.startsWith('/') ? `${BACKEND_URL}${url}` : url,
            })));
            break;
          } else if (poll.data.status === 'failed') {
            setBlogMsg('Gagal generate gambar: ' + (poll.data.error || ''));
            break;
          }
        } catch { /* continue polling */ }
      }
    } catch (err) {
      setBlogMsg('Gagal generate gambar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setEditImageLoading(false);
    }
  };

  const handleSelectEditImage = async (idx) => {
    setEditImageSelected(idx);
    const img = editImageOptions[idx];
    if (!img || !editingImageArticle) return;
    try {
      await axios.put(`${BACKEND_URL}/api/blog/admin/article/${editingImageArticle.id}/featured-image`, {
        image_url: img.url.replace(BACKEND_URL, ''),
      });
      setBlogMsg('Gambar berhasil diperbarui!');
      fetchBlogArticles();
    } catch (err) {
      setBlogMsg('Gagal update gambar: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEditArticle = async (article) => {
    // Fetch full article content for editing
    try {
      const res = await axios.get(`${BACKEND_URL}/api/blog/admin/articles`);
      const full = res.data.articles.find(a => a.id === article.id);
      if (full) {
        // Fetch content from detail endpoint
        const detailRes = await axios.get(`${BACKEND_URL}/api/blog/articles/${article.id}`);
        setEditingArticle({ ...full, content: detailRes.data.article?.content || '' });
      }
    } catch {
      setEditingArticle(article);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddr) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/blog/admin/test-email?email=${encodeURIComponent(testEmailAddr)}`);
      setTestEmailResult(res.data);
    } catch (err) { setTestEmailResult({ status: 'failed', message: err.response?.data?.detail || err.message }); }
  };

  const handleTriggerDigest = async () => {
    setTriggeringDigest(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/blog/admin/trigger-digest`);
      const r = res.data.result || {};
      const aiLabel = r.ai_summary_generated ? ' (AI Summary)' : ' (Plain)';
      setBlogMsg(`Weekly Digest terkirim${aiLabel}! Sent: ${r.sent || 0}, Failed: ${r.failed || 0}, Artikel: ${r.articles_count || 0}`);
      fetchNotificationData();
    } catch (err) { setBlogMsg('Gagal trigger digest: ' + (err.response?.data?.detail || err.message)); }
    finally { setTriggeringDigest(false); }
  };

  const handleFbMonitor = async () => {
    setFbMonitoring(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/blog/admin/trigger-fb-monitor`);
      const r = res.data.result || {};
      setBlogMsg(`Facebook Monitor: ${r.posts_found} posts ditemukan, ${r.articles_generated} artikel di-generate`);
      fetchBlogArticles();
      fetchFbMonitorStatus();
    } catch (err) { setBlogMsg('Gagal monitor FB: ' + (err.response?.data?.detail || err.message)); }
    finally { setFbMonitoring(false); }
  };

  const handleFbConfigUpdate = async (updates) => {
    setFbConfigSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/blog/admin/fb-monitor-config`, updates);
      await fetchFbMonitorStatus();
      setBlogMsg('Konfigurasi FB Monitor berhasil diperbarui');
    } catch (err) { setBlogMsg('Gagal update config: ' + (err.response?.data?.detail || err.message)); }
    finally { setFbConfigSaving(false); }
  };

  return (
    <div className="admin-section" data-testid="admin-blog-section">
      <h2 className="section-heading"><BookOpen size={20} /> Kelola Blog</h2>

      {/* Blog Scheduler Status */}
      <div className="stat-card" style={{marginBottom: '1.5rem'}}>
        <h3 style={{marginBottom: '0.75rem'}}><Clock size={18} /> Auto-Publish Scheduler</h3>
        {blogSchedulerStatus ? (
          <div style={{display:'flex',flexDirection:'column',gap:'0.5rem',fontSize:'0.9rem'}}>
            <div style={{display:'flex',gap:'1.5rem',flexWrap:'wrap'}}>
              <span>Status: <strong style={{color: blogSchedulerStatus.active ? '#4ade80' : '#f87171'}}>{blogSchedulerStatus.active ? 'Aktif' : 'Nonaktif'}</strong></span>
              <span>Jadwal: <strong style={{color:'#d9fb06'}}>{blogSchedulerStatus.schedule}</strong></span>
            </div>
            <div style={{display:'flex',gap:'1.5rem',flexWrap:'wrap',color:'#A1A1AA',fontSize:'0.8rem'}}>
              {blogSchedulerStatus.current_time_wib && <span>Waktu: {blogSchedulerStatus.current_time_wib}</span>}
              {blogSchedulerStatus.next_run && <span>Publish berikutnya: {new Date(blogSchedulerStatus.next_run).toLocaleString('id-ID', {weekday:'long', day:'numeric', month:'short', hour:'2-digit', minute:'2-digit'})} WIB</span>}
              {blogSchedulerStatus.last_run && <span>Terakhir: {new Date(blogSchedulerStatus.last_run).toLocaleString('id-ID')}</span>}
            </div>
            <div style={{display:'flex',gap:'0.5rem',marginTop:'0.5rem'}}>
              <button onClick={handleTriggerBlogGenerate} disabled={triggeringBlog} className="btn-primary" style={{padding:'0.5rem 1.25rem'}} data-testid="trigger-blog-generate">
                <Play size={14} /> {triggeringBlog ? 'Generating...' : 'Generate Sekarang (Manual)'}
              </button>
              <button onClick={handleTriggerDigest} disabled={triggeringDigest} className="btn-primary" style={{padding:'0.5rem 1.25rem',background:'rgba(255,255,255,0.08)',color:'#EDEDED'}} data-testid="trigger-digest-btn">
                <Bell size={14} /> {triggeringDigest ? 'Sending...' : 'Trigger Weekly Digest'}
              </button>
            </div>
            {digestStatus && (
              <div style={{fontSize:'0.8rem',color:'#71717A',marginTop:'0.25rem'}}>
                Digest subscribers: <strong style={{color:'#d9fb06'}}>{digestStatus.digest_subscribers}</strong> / {digestStatus.total_active_subscribers} aktif
                {digestStatus.last_digest && (
                  <span>
                    {' '} — Terakhir: {new Date(digestStatus.last_digest.created_at).toLocaleString('id-ID')}
                    {digestStatus.last_digest.ai_summary_generated && <span style={{color:'#4ade80',marginLeft:'6px',fontSize:'0.75rem',background:'rgba(74,222,128,0.1)',padding:'1px 6px',borderRadius:'8px'}}>AI Summary</span>}
                    {digestStatus.last_digest.ai_summary_generated === false && <span style={{color:'#f87171',marginLeft:'6px',fontSize:'0.75rem',background:'rgba(248,113,113,0.1)',padding:'1px 6px',borderRadius:'8px'}}>Plain</span>}
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
          <p style={{color:'#A1A1AA',fontSize:'0.9rem'}}>Memuat status scheduler...</p>
        )}
      </div>

      {/* Facebook Monitor - Enhanced */}
      <div className="stat-card" style={{marginBottom: '1.5rem'}}>
        <h3 style={{marginBottom: '0.75rem', display:'flex', alignItems:'center', gap:'0.5rem'}}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#4267b2"><path d="M24 12.073c0-6.627-5.373-12-12-12S0 5.446 0 12.073c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.875v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          Facebook Monitor
          {fbMonitorStatus && (
            <span style={{
              fontSize:'0.7rem', padding:'2px 8px', borderRadius:'12px', marginLeft:'auto',
              background: fbMonitorStatus.enabled ? 'rgba(74,222,128,0.15)' : 'rgba(248,113,113,0.15)',
              color: fbMonitorStatus.enabled ? '#4ade80' : '#f87171',
            }}>
              {fbMonitorStatus.enabled ? 'AKTIF' : 'NONAKTIF'}
            </span>
          )}
        </h3>

        {/* Status & Config */}
        {fbMonitorStatus && (
          <div style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'0.75rem',padding:'1rem',marginBottom:'1rem'}} data-testid="fb-monitor-status-panel">
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:'0.75rem',marginBottom:'1rem'}}>
              <div style={{textAlign:'center',background:'rgba(66,103,178,0.1)',borderRadius:'0.5rem',padding:'0.75rem'}}>
                <p style={{fontSize:'1.25rem',fontWeight:700,color:'#4267b2',margin:'0 0 2px'}}>{fbMonitorStatus.fb_articles?.total || 0}</p>
                <p style={{fontSize:'0.7rem',color:'#71717A',margin:0}}>Total Artikel FB</p>
              </div>
              <div style={{textAlign:'center',background:'rgba(251,191,36,0.1)',borderRadius:'0.5rem',padding:'0.75rem'}}>
                <p style={{fontSize:'1.25rem',fontWeight:700,color:'#fbbf24',margin:'0 0 2px'}}>{fbMonitorStatus.fb_articles?.draft || 0}</p>
                <p style={{fontSize:'0.7rem',color:'#71717A',margin:0}}>Draft</p>
              </div>
              <div style={{textAlign:'center',background:'rgba(74,222,128,0.1)',borderRadius:'0.5rem',padding:'0.75rem'}}>
                <p style={{fontSize:'1.25rem',fontWeight:700,color:'#4ade80',margin:'0 0 2px'}}>{fbMonitorStatus.fb_articles?.published || 0}</p>
                <p style={{fontSize:'0.7rem',color:'#71717A',margin:0}}>Published</p>
              </div>
              <div style={{textAlign:'center',background:'rgba(255,255,255,0.05)',borderRadius:'0.5rem',padding:'0.75rem'}}>
                <p style={{fontSize:'1.25rem',fontWeight:700,color:'#EDEDED',margin:'0 0 2px'}}>
                  {fbMonitorStatus.has_api_token ? 'API' : 'Scrape'}
                </p>
                <p style={{fontSize:'0.7rem',color:'#71717A',margin:0}}>Metode</p>
              </div>
            </div>

            {/* Config Controls */}
            <div style={{display:'flex',gap:'0.75rem',flexWrap:'wrap',alignItems:'center',marginBottom:'0.75rem'}}>
              <button
                onClick={() => handleFbConfigUpdate({ enabled: !fbMonitorStatus.enabled })}
                disabled={fbConfigSaving}
                style={{
                  display:'flex',alignItems:'center',gap:'0.375rem',padding:'0.375rem 0.75rem',borderRadius:'0.375rem',border:'none',cursor:'pointer',fontSize:'0.8rem',
                  background: fbMonitorStatus.enabled ? 'rgba(248,113,113,0.15)' : 'rgba(74,222,128,0.15)',
                  color: fbMonitorStatus.enabled ? '#f87171' : '#4ade80',
                }}
                data-testid="fb-toggle-enabled"
              >
                <Power size={14} /> {fbMonitorStatus.enabled ? 'Nonaktifkan' : 'Aktifkan'}
              </button>

              <div style={{display:'flex',alignItems:'center',gap:'0.375rem',fontSize:'0.8rem',color:'#A1A1AA'}}>
                <Settings size={14} />
                <span>Interval:</span>
                <select
                  value={fbMonitorStatus.interval_hours || 12}
                  onChange={e => handleFbConfigUpdate({ interval_hours: parseInt(e.target.value) })}
                  style={{background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'0.25rem',padding:'0.25rem 0.5rem',color:'#EDEDED',fontSize:'0.8rem'}}
                  data-testid="fb-interval-select"
                >
                  <option value={6}>6 jam</option>
                  <option value={12}>12 jam</option>
                  <option value={24}>24 jam</option>
                  <option value={48}>48 jam</option>
                </select>
              </div>

              <label style={{display:'flex',alignItems:'center',gap:'0.375rem',fontSize:'0.8rem',color:'#A1A1AA',cursor:'pointer'}}>
                <input
                  type="checkbox"
                  checked={fbMonitorStatus.auto_publish || false}
                  onChange={e => handleFbConfigUpdate({ auto_publish: e.target.checked })}
                  style={{accentColor:'#d9fb06'}}
                  data-testid="fb-auto-publish-toggle"
                />
                Auto-publish
              </label>
            </div>

            {/* Info row */}
            <div style={{display:'flex',gap:'1rem',flexWrap:'wrap',fontSize:'0.75rem',color:'#71717A'}}>
              {fbMonitorStatus.last_run && <span>Terakhir: {new Date(fbMonitorStatus.last_run).toLocaleString('id-ID')}</span>}
              {fbMonitorStatus.last_result && (
                <span>
                  Hasil: {fbMonitorStatus.last_result.posts_found || 0} posts,{' '}
                  {fbMonitorStatus.last_result.articles_generated || 0} artikel
                </span>
              )}
              {!fbMonitorStatus.has_api_token && (
                <span style={{color:'#fbbf24'}}>
                  Tip: Set FB_ACCESS_TOKEN di .env untuk akses Graph API yang lebih reliable
                </span>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{display:'flex',gap:'0.5rem',marginBottom:'0.75rem',flexWrap:'wrap'}}>
          <button onClick={handleFbMonitor} disabled={fbMonitoring} className="btn-primary" style={{padding:'0.5rem 1.25rem'}} data-testid="trigger-fb-monitor">
            <RefreshCw size={14} className={fbMonitoring ? 'animate-spin' : ''} /> {fbMonitoring ? 'Scanning...' : 'Scan Facebook Sekarang'}
          </button>
        </div>

        {/* Recent Monitor Logs */}
        {fbMonitorStatus?.logs?.length > 0 && (
          <div style={{marginTop:'1rem'}} data-testid="fb-monitor-logs">
            <h4 style={{color:'#A1A1AA',fontSize:'0.85rem',marginBottom:'0.5rem'}}>Riwayat Monitoring</h4>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{borderBottom:'1px solid rgba(255,255,255,0.08)'}}>
                    <th style={{textAlign:'left',padding:'0.5rem',fontSize:'0.75rem',color:'#71717A'}}>Waktu</th>
                    <th style={{textAlign:'center',padding:'0.5rem',fontSize:'0.75rem',color:'#71717A'}}>Metode</th>
                    <th style={{textAlign:'center',padding:'0.5rem',fontSize:'0.75rem',color:'#71717A'}}>Posts</th>
                    <th style={{textAlign:'center',padding:'0.5rem',fontSize:'0.75rem',color:'#71717A'}}>Artikel</th>
                    <th style={{textAlign:'left',padding:'0.5rem',fontSize:'0.75rem',color:'#71717A'}}>Judul</th>
                  </tr>
                </thead>
                <tbody>
                  {fbMonitorStatus.logs.map((log, i) => (
                    <tr key={log.id || i} style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                      <td style={{padding:'0.5rem',fontSize:'0.75rem',color:'#A1A1AA',whiteSpace:'nowrap'}}>
                        {log.created_at ? new Date(log.created_at).toLocaleString('id-ID', {day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'}) : '-'}
                      </td>
                      <td style={{padding:'0.5rem',fontSize:'0.75rem',textAlign:'center'}}>
                        <span style={{
                          background: log.method === 'graph_api' ? 'rgba(66,103,178,0.15)' : 'rgba(255,255,255,0.08)',
                          color: log.method === 'graph_api' ? '#93b4f0' : '#A1A1AA',
                          padding:'2px 6px',borderRadius:'8px',fontSize:'0.7rem'
                        }}>
                          {log.method === 'graph_api' ? 'API' : 'Scrape'}
                        </span>
                      </td>
                      <td style={{padding:'0.5rem',fontSize:'0.8rem',textAlign:'center',color:'#EDEDED'}}>{log.posts_found}</td>
                      <td style={{padding:'0.5rem',fontSize:'0.8rem',textAlign:'center',color: log.articles_generated > 0 ? '#4ade80' : '#71717A',fontWeight: log.articles_generated > 0 ? 600 : 400}}>
                        {log.articles_generated}
                      </td>
                      <td style={{padding:'0.5rem',fontSize:'0.75rem',color:'#A1A1AA',maxWidth:'200px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {log.articles_titles?.join(', ') || (log.error ? `Error: ${log.error}` : '-')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Email Delivery & Diagnostics */}
      <div className="stat-card" style={{marginBottom: '1.5rem'}}>
        <h3 style={{marginBottom: '0.75rem'}}><ShieldCheck size={18} /> Email Delivery & Diagnostics</h3>
        <div style={{display:'flex',gap:'0.5rem',alignItems:'center',flexWrap:'wrap',marginBottom:'1rem'}}>
          <input type="email" value={testEmailAddr} onChange={e=>setTestEmailAddr(e.target.value)} placeholder="Email tujuan test" style={{flex:1,background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'0.375rem',padding:'0.5rem 0.75rem',color:'#EDEDED',fontSize:'0.9rem'}} data-testid="test-email-input" />
          <button onClick={handleTestEmail} className="btn-primary" style={{padding:'0.5rem 1rem'}} data-testid="test-email-btn"><Send size={14} /> Kirim Test</button>
          <button onClick={fetchEmailDiagnostics} className="btn-primary" style={{padding:'0.5rem 1rem',background:'rgba(255,255,255,0.08)',color:'#EDEDED'}} disabled={diagLoading} data-testid="run-diagnostics-btn">
            <ShieldCheck size={14} /> {diagLoading ? 'Cek...' : 'Diagnostik'}
          </button>
        </div>
        {testEmailResult && (
          <div style={{marginBottom:'0.75rem',padding:'0.75rem',background: testEmailResult.status === 'success' ? 'rgba(74,222,128,0.1)' : 'rgba(239,68,68,0.1)',borderRadius:'0.375rem',fontSize:'0.85rem'}}>
            <p><strong>Status:</strong> {testEmailResult.status}</p>
            <p>{testEmailResult.message}</p>
            {testEmailResult.sender_email && <p><strong>Sender:</strong> {testEmailResult.sender_email}</p>}
          </div>
        )}
        {emailDiagnostics && (
          <div style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'0.75rem',padding:'1rem',fontSize:'0.85rem'}} data-testid="email-diagnostics-panel">
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:'0.75rem'}}>
              <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                {emailDiagnostics.sendgrid_configured ? <CheckCircle size={16} color="#4ade80" /> : <XCircle size={16} color="#f87171" />}
                <span style={{color: emailDiagnostics.sendgrid_configured ? '#4ade80' : '#f87171'}}>SendGrid: {emailDiagnostics.sendgrid_configured ? 'Terkonfigurasi' : 'Belum'}</span>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                {emailDiagnostics.sendgrid_api_status === 'connected' || emailDiagnostics.sendgrid_api_status === 'key_valid_limited_scope' ? <CheckCircle size={16} color="#4ade80" /> : <AlertCircle size={16} color="#fbbf24" />}
                <span style={{color:'#A1A1AA'}}>API: {emailDiagnostics.sendgrid_api_status}</span>
              </div>
              <div><span style={{color:'#71717A'}}>Sender:</span> <span style={{color:'#EDEDED'}}>{emailDiagnostics.sender_email}</span></div>
              <div><span style={{color:'#71717A'}}>Subscriber aktif:</span> <span style={{color:'#d9fb06',fontWeight:700}}>{emailDiagnostics.active_subscribers}</span></div>
              <div><span style={{color:'#71717A'}}>Total:</span> <span style={{color:'#EDEDED'}}>{emailDiagnostics.total_subscribers}</span></div>
              <div><span style={{color:'#71717A'}}>Unsubscribed:</span> <span style={{color:'#f87171'}}>{emailDiagnostics.unsubscribed}</span></div>
            </div>
          </div>
        )}
      </div>

      {/* Notification Stats */}
      {notifStats && (
        <div className="stat-card" style={{marginBottom: '1.5rem'}}>
          <h3 style={{marginBottom: '0.75rem'}}><Bell size={18} /> Statistik Notifikasi</h3>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:'0.75rem',marginBottom:'1rem'}}>
            <div style={{background:'rgba(217,251,6,0.08)',borderRadius:'0.75rem',padding:'1rem',textAlign:'center'}}>
              <p style={{fontSize:'1.5rem',fontWeight:700,color:'#d9fb06',margin:'0 0 4px'}}>{notifStats.total_emails_sent}</p>
              <p style={{fontSize:'0.75rem',color:'#71717A',margin:0}}>Email Terkirim</p>
            </div>
            <div style={{background:'rgba(239,68,68,0.08)',borderRadius:'0.75rem',padding:'1rem',textAlign:'center'}}>
              <p style={{fontSize:'1.5rem',fontWeight:700,color:'#f87171',margin:'0 0 4px'}}>{notifStats.total_emails_failed}</p>
              <p style={{fontSize:'0.75rem',color:'#71717A',margin:0}}>Gagal Kirim</p>
            </div>
            <div style={{background:'rgba(255,255,255,0.05)',borderRadius:'0.75rem',padding:'1rem',textAlign:'center'}}>
              <p style={{fontSize:'1.5rem',fontWeight:700,color:'#EDEDED',margin:'0 0 4px'}}>{notifStats.article_notifications}</p>
              <p style={{fontSize:'0.75rem',color:'#71717A',margin:0}}>Notif Artikel</p>
            </div>
            <div style={{background:'rgba(255,255,255,0.05)',borderRadius:'0.75rem',padding:'1rem',textAlign:'center'}}>
              <p style={{fontSize:'1.5rem',fontWeight:700,color:'#EDEDED',margin:'0 0 4px'}}>{notifStats.reply_notifications}</p>
              <p style={{fontSize:'0.75rem',color:'#71717A',margin:0}}>Notif Balasan</p>
            </div>
          </div>
          {notifLogs.length > 0 && (
            <div data-testid="notification-logs-table">
              <h4 style={{color:'#A1A1AA',fontSize:'0.85rem',marginBottom:'0.5rem'}}>Log Notifikasi Terbaru</h4>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead>
                    <tr style={{borderBottom:'1px solid rgba(255,255,255,0.08)'}}>
                      <th style={{textAlign:'left',padding:'0.5rem',fontSize:'0.75rem',color:'#71717A'}}>Tipe</th>
                      <th style={{textAlign:'left',padding:'0.5rem',fontSize:'0.75rem',color:'#71717A'}}>Artikel</th>
                      <th style={{textAlign:'center',padding:'0.5rem',fontSize:'0.75rem',color:'#71717A'}}>Terkirim</th>
                      <th style={{textAlign:'center',padding:'0.5rem',fontSize:'0.75rem',color:'#71717A'}}>Gagal</th>
                      <th style={{textAlign:'right',padding:'0.5rem',fontSize:'0.75rem',color:'#71717A'}}>Waktu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notifLogs.map((log, i) => (
                      <tr key={log.id || i} style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                        <td style={{padding:'0.5rem',fontSize:'0.8rem'}}>
                          <span style={{background: log.type === 'new_article' ? 'rgba(217,251,6,0.15)' : 'rgba(147,197,253,0.15)', color: log.type === 'new_article' ? '#d9fb06' : '#93c5fd', padding:'2px 8px',borderRadius:'12px',fontSize:'0.7rem'}}>{log.type === 'new_article' ? 'Artikel' : log.type === 'weekly_digest' ? 'Digest' : 'Balasan'}</span>
                        </td>
                        <td style={{padding:'0.5rem',fontSize:'0.8rem',color:'#EDEDED',maxWidth:'200px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{log.article_title || log.article_id || '-'}</td>
                        <td style={{padding:'0.5rem',fontSize:'0.8rem',textAlign:'center',color:'#4ade80'}}>{log.sent ?? (log.sent === true ? 1 : 0)}</td>
                        <td style={{padding:'0.5rem',fontSize:'0.8rem',textAlign:'center',color: (log.failed || 0) > 0 ? '#f87171' : '#71717A'}}>{log.failed || 0}</td>
                        <td style={{padding:'0.5rem',fontSize:'0.75rem',textAlign:'right',color:'#71717A'}}>{log.created_at ? new Date(log.created_at).toLocaleString('id-ID') : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Social Media Article Creator */}
      <SocialArticleCreator
        onArticleCreated={fetchBlogArticles}
        blogMsg={blogMsg}
        setBlogMsg={setBlogMsg}
      />

      {blogMsg && <p style={{marginTop:'-1rem',marginBottom:'1rem',color: blogMsg.includes('berhasil') || blogMsg.includes('terkirim') || blogMsg.includes('disalin') ? '#4ade80' : '#f87171',fontSize:'0.9rem',padding:'0 0.25rem'}} data-testid="blog-status-message">{blogMsg}</p>}

      {/* Articles List */}
      <div className="stat-card">
        {/* Stats summary */}
        <div style={{display:'flex',gap:'1rem',marginBottom:'1rem',flexWrap:'wrap'}}>
          {[
            { key: 'all', label: 'Semua', count: blogArticles.length, color: '#EDEDED' },
            { key: 'published', label: 'Published', count: blogArticles.filter(a => a.status === 'published').length, color: '#4ade80' },
            { key: 'draft', label: 'Draft', count: blogArticles.filter(a => a.status === 'draft').length, color: '#fbbf24' },
            { key: 'archived', label: 'Archived', count: blogArticles.filter(a => a.status === 'archived').length, color: '#94a3b8' },
          ].map(f => (
            <button key={f.key} onClick={() => setArticleFilter(f.key)} data-testid={`filter-${f.key}`} style={{
              display:'flex', alignItems:'center', gap:'0.375rem', padding:'0.4rem 0.85rem',
              borderRadius:'2rem', border: articleFilter === f.key ? `1px solid ${f.color}` : '1px solid rgba(255,255,255,0.08)',
              background: articleFilter === f.key ? `${f.color}15` : 'transparent',
              color: f.color, fontSize:'0.8rem', fontWeight: articleFilter === f.key ? 600 : 400, cursor:'pointer',
            }}>
              {f.label} <span style={{fontWeight:700}}>{f.count}</span>
            </button>
          ))}
        </div>

        {blogArticles.length === 0 ? (
          <div className="scheduler-empty"><BookOpen size={48} /><p>Belum ada artikel. Generate artikel pertama di atas!</p></div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:'0.75rem'}}>
            {blogArticles
              .filter(a => articleFilter === 'all' || a.status === articleFilter)
              .map(a => {
                const isExclusive = a.exclusive_until && new Date(a.exclusive_until) > new Date();
                return (
              <div key={a.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'0.75rem',padding:'1rem 1.25rem',gap:'1rem',flexWrap:'wrap'}}>
                <div style={{display:'flex',gap:'1rem',alignItems:'center',flex:1,minWidth:'200px'}}>
                  {a.featured_image && <img src={a.featured_image?.startsWith('/api') ? `${BACKEND_URL}${a.featured_image}` : a.featured_image} alt="" style={{width:'48px',height:'48px',objectFit:'cover',borderRadius:'0.375rem',flexShrink:0}} />}
                  <div>
                    <h4 style={{color:'#EDEDED',fontFamily:"'Playfair Display',serif",fontSize:'1rem',marginBottom:'0.25rem'}}>{a.title}</h4>
                    <div style={{display:'flex',gap:'0.75rem',fontSize:'0.75rem',color:'#71717A',flexWrap:'wrap',alignItems:'center'}}>
                      <span>{a.category}</span>
                      <span>{a.views || 0} views</span>
                      <span>{a.likes || 0} likes</span>
                      <span>{new Date(a.created_at).toLocaleDateString('id-ID')}</span>
                      {isExclusive && (
                        <span style={{background:'rgba(217,251,6,0.12)',color:'#d9fb06',padding:'1px 6px',borderRadius:'8px',fontSize:'0.65rem',fontWeight:600}}>
                          Member Only s/d {new Date(a.exclusive_until).toLocaleDateString('id-ID')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{display:'flex',gap:'0.375rem',alignItems:'center',flexWrap:'wrap'}}>
                  <span style={{
                    padding:'0.2rem 0.6rem',borderRadius:'2rem',fontSize:'0.7rem',fontWeight:600,
                    background: a.status === 'published' ? 'rgba(74,222,128,0.15)' : a.status === 'draft' ? 'rgba(251,191,36,0.15)' : 'rgba(148,163,184,0.15)',
                    color: a.status === 'published' ? '#4ade80' : a.status === 'draft' ? '#fbbf24' : '#94a3b8'
                  }}>{a.status}</span>
                  <button onClick={() => handleEditArticle(a)} title="Edit Artikel" style={{background:'rgba(147,197,253,0.12)',border:'none',color:'#93c5fd',borderRadius:'0.375rem',padding:'0.375rem',cursor:'pointer',display:'flex'}} data-testid={`edit-article-${a.id}`}>
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => handleEditImage(a)} title="Edit Gambar" style={{background:'rgba(217,251,6,0.12)',border:'none',color:'#d9fb06',borderRadius:'0.375rem',padding:'0.375rem',cursor:'pointer',display:'flex'}} data-testid={`edit-image-${a.id}`}>
                    <ImageIcon size={16} />
                  </button>
                  {a.status === 'draft' && (
                    <button onClick={() => handlePublishArticle(a.id)} title="Publish Sekarang" data-testid={`publish-${a.id}`} style={{
                      background:'rgba(74,222,128,0.15)',border:'none',color:'#4ade80',borderRadius:'0.375rem',
                      padding:'0.25rem 0.6rem',cursor:'pointer',display:'flex',alignItems:'center',gap:'0.25rem',fontSize:'0.75rem',fontWeight:600,
                    }}>
                      <Eye size={14} /> Publish
                    </button>
                  )}
                  {a.status === 'published' && (
                    <button onClick={() => handleUnpublishArticle(a.id)} title="Kembalikan ke Draft" data-testid={`unpublish-${a.id}`} style={{
                      background:'rgba(251,191,36,0.1)',border:'none',color:'#fbbf24',borderRadius:'0.375rem',
                      padding:'0.25rem 0.6rem',cursor:'pointer',display:'flex',alignItems:'center',gap:'0.25rem',fontSize:'0.75rem',
                    }}>
                      <EyeOff size={14} /> Draft
                    </button>
                  )}
                  {a.status !== 'archived' && (
                    <button onClick={() => handleArchiveArticle(a.id)} title="Arsipkan" data-testid={`archive-${a.id}`} style={{
                      background:'rgba(148,163,184,0.1)',border:'none',color:'#94a3b8',borderRadius:'0.375rem',
                      padding:'0.25rem 0.6rem',cursor:'pointer',display:'flex',alignItems:'center',gap:'0.25rem',fontSize:'0.75rem',
                    }}>
                      <Archive size={14} /> Arsip
                    </button>
                  )}
                  {a.status === 'archived' && (
                    <button onClick={() => handlePublishArticle(a.id)} title="Publish Kembali" data-testid={`republish-${a.id}`} style={{
                      background:'rgba(74,222,128,0.15)',border:'none',color:'#4ade80',borderRadius:'0.375rem',
                      padding:'0.25rem 0.6rem',cursor:'pointer',display:'flex',alignItems:'center',gap:'0.25rem',fontSize:'0.75rem',fontWeight:600,
                    }}>
                      <Eye size={14} /> Publish
                    </button>
                  )}
                  <button onClick={() => handleDeleteArticle(a.id)} title="Hapus Permanen" style={{background:'rgba(239,68,68,0.15)',border:'none',color:'#f87171',borderRadius:'0.375rem',padding:'0.375rem',cursor:'pointer',display:'flex'}} data-testid={`delete-${a.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Article Editor Modal */}
      {editingArticle && (
        <ArticleEditor
          article={editingArticle}
          onClose={() => setEditingArticle(null)}
          onSaved={() => { fetchBlogArticles(); }}
        />
      )}

      {/* Image Edit Modal */}
      {editingImageArticle && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(8px)',
        }} data-testid="image-edit-modal" onClick={(e) => { if (e.target === e.currentTarget && !editImageLoading) setEditingImageArticle(null); }}>
          <div style={{
            background: '#141414', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '1rem',
            padding: '1.5rem', maxWidth: '700px', width: '90%', maxHeight: '85vh', overflow: 'auto',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h3 style={{ color: '#EDEDED', fontSize: '1rem', margin: 0 }}>
                Edit Gambar: {editingImageArticle.title?.substring(0, 50)}...
              </h3>
              {!editImageLoading && (
                <button onClick={() => setEditingImageArticle(null)} style={{ background: 'none', border: 'none', color: '#71717A', cursor: 'pointer', display: 'flex' }}>
                  <X size={20} />
                </button>
              )}
            </div>

            {editImageLoading && (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <Loader2 size={28} color="#d9fb06" style={{ animation: 'spin 1s linear infinite' }} />
                <p style={{ color: '#EDEDED', fontSize: '0.85rem', marginTop: '0.75rem' }}>Membuat 3 pilihan gambar ilustrasi...</p>
                <div style={{ margin: '0.75rem auto', maxWidth: '250px', height: '3px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${editImageProgress}%`, background: '#d9fb06', borderRadius: '2px', transition: 'width 0.5s ease' }} />
                </div>
                <p style={{ color: '#71717A', fontSize: '0.75rem' }}>
                  {editImageProgress < 30 ? 'Menganalisis artikel...' : editImageProgress < 60 ? 'Generating gambar 1/3...' : editImageProgress < 85 ? 'Generating gambar 2/3...' : 'Finishing gambar 3/3...'}
                </p>
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              </div>
            )}

            {!editImageLoading && editImageOptions.length > 0 && (
              <>
                <p style={{ color: '#A1A1AA', fontSize: '0.8rem', marginBottom: '1rem' }}>Klik gambar untuk memilih sebagai featured image:</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
                  {editImageOptions.map((img, idx) => (
                    <div
                      key={img.id}
                      onClick={() => handleSelectEditImage(idx)}
                      data-testid={`edit-image-option-${idx}`}
                      style={{
                        position: 'relative', cursor: 'pointer', borderRadius: '0.625rem', overflow: 'hidden',
                        border: editImageSelected === idx ? '2px solid #d9fb06' : '2px solid rgba(255,255,255,0.08)',
                        transition: 'all 0.2s ease',
                        boxShadow: editImageSelected === idx ? '0 0 16px rgba(217,251,6,0.2)' : 'none',
                      }}
                    >
                      <img src={img.url} alt={`Opsi ${idx + 1}`} style={{ width: '100%', aspectRatio: '16/10', objectFit: 'cover', display: 'block' }} />
                      {editImageSelected === idx && (
                        <div style={{
                          position: 'absolute', top: '0.4rem', right: '0.4rem',
                          width: '22px', height: '22px', borderRadius: '50%',
                          background: '#d9fb06', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <CheckCircle size={14} color="#0A0A0A" />
                        </div>
                      )}
                      <div style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        padding: '0.35rem', textAlign: 'center',
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                        color: '#A1A1AA', fontSize: '0.7rem',
                      }}>Opsi {idx + 1}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => handleEditImage(editingImageArticle)} style={{
                    display: 'flex', alignItems: 'center', gap: '0.25rem',
                    padding: '0.4rem 0.75rem', borderRadius: '0.375rem', border: 'none', cursor: 'pointer',
                    fontSize: '0.8rem', background: 'rgba(255,255,255,0.08)', color: '#A1A1AA',
                  }} data-testid="regenerate-edit-images-btn">
                    <RefreshCw size={12} /> Generate Ulang
                  </button>
                  <button onClick={() => setEditingImageArticle(null)} style={{
                    marginLeft: 'auto', padding: '0.4rem 0.75rem', borderRadius: '0.375rem', border: 'none', cursor: 'pointer',
                    fontSize: '0.8rem', fontWeight: 500, background: '#d9fb06', color: '#0A0A0A',
                  }}>Selesai</button>
                </div>
              </>
            )}

            {!editImageLoading && editImageOptions.length === 0 && (
              <div style={{ textAlign: 'center', padding: '1rem', color: '#71717A' }}>
                <p>Gambar gagal di-generate. Silakan coba lagi.</p>
                <button onClick={() => handleEditImage(editingImageArticle)} style={{
                  marginTop: '0.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                  padding: '0.5rem 1rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer',
                  fontSize: '0.8rem', fontWeight: 500, background: '#d9fb06', color: '#0A0A0A',
                }}>
                  <RefreshCw size={14} /> Coba Lagi
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogTab;
